import AsyncTable from "components/AsyncTable"
import { Badge, Divider, Flex, Input, InputGroup, InputLeftAddon, Text, useDisclosure } from "@chakra-ui/react"
import StatusIndicator from "components/StatusIndicator"
import { riskColor, riskLabel } from "enums/labelRisks"
import { useLazyPaginateLives, usePaginateLives } from "hooks/useLives"
import { useSelector } from "react-redux"
import { findLastMetricInAssessmentsByTag } from "functions/lives/metrics"
import { State } from "state/reducers"
import { CalendarDots, WhatsappLogo } from "@phosphor-icons/react"
import SendMessage from "./components/SendMessage"
import dateFormat, { splitDateFormat } from "functions/dateFormat"
import { useState } from "react"
import TableFilterBox from "components/TableFilterBox"
import { FormProvider, useForm } from "react-hook-form"
import InputMask from "react-input-mask";
import { format } from "date-fns"

interface Props {
    highRisk: boolean
}

const LivesTable = ({ highRisk = false }: Props) => {
    const header = [
        {
            label: 'Nome',
            acessor: 'name',
        },
        {
            label: 'Nº Carteirinha',
            acessor: 'document',
        },
        {
            label: 'CPF',
            acessor: 'cpf',
        },
        {
            label: 'Telefone',
            acessor: 'phone',
        },
        {
            label: 'Status',
            acessor: 'status',
            custom: (value: any): React.ReactNode => <StatusIndicator isActive={value} size="sm" />
        },
        {
            label: 'Risco',
            acessor: 'risk',
            custom: (value: any): React.ReactNode => {
                if(highRisk) return <Badge colorScheme="red">Alto</Badge>
                if(value == null) return <Text>N/A</Text>
                let color = riskColor[value as keyof typeof riskColor];
                let label = riskLabel[value as keyof typeof riskLabel];
                return <Badge colorScheme={color}>{label}</Badge>
            }
        },
        {
            label: 'Proximo Formulário',
            acessor: 'next_event',
            custom: (value: any) => {
                if(value == null) return <Text>N/A</Text>
                return <Text>{value.name}</Text>
            }
        },
        {
            label: 'Data de criação',
            acessor: 'created_at',
            custom: (value: any) => <Text>{dateFormat(value)}</Text>
        },
    ]

    const methods = useForm<any>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        defaultValues: {
            "start_date": "",
            "end_date": "",
        }
    });

    const { register, getValues } = methods;

    const { loading, refetch } = usePaginateLives(highRisk);
    const [reloading, setReloading] = useState<boolean>(false);
    // const [repaginate, { loading: reloading }] = useLazyPaginateLives(highRisk)

    const { paginatorInfo, data } = useSelector(({ lives }: State) => lives)

    const { isOpen, onOpen, onClose } = useDisclosure()

    const handleFilter = async () => {
        const filters = getValues();
        
        if(filters.start_date != '')
            filters.start_date = splitDateFormat(filters.start_date);

        if(filters.end_date != '')
            filters.end_date = splitDateFormat(filters.end_date);

        setReloading(true);
        await refetch({
            page: 1,
            filters: JSON.stringify(filters)
        })
        setReloading(false);
    }

    const handleResetFilter = async () => {
        setReloading(true);
        await refetch({
            page: 1,
            filters: ""
        })
        setReloading(false);
    }

    const onPaginationChange = async (page: number, per_page: number, search: string = '') => {
        setReloading(true);
        await refetch({
            page,
            first: per_page,
            search
        })
        setReloading(false);
    }

    const filterFields = [
        {
            name: "start_date",
            label: "Início Data de criação",
            colSpan: 1,
            component: 
            <InputGroup>
                <InputLeftAddon><CalendarDots size={22} /></InputLeftAddon>
                <Input
                    {...register('start_date')}
                    as={InputMask}
                    mask="99/99/9999"
                    placeholder='__/__/____'
                    borderRadius="8px"
                    fontSize="md"
                />
            </InputGroup>
        },
        {
            name: "end_Date",
            label: "Final Data de criação",
            colSpan: 1,
            component: 
            <InputGroup>
                <InputLeftAddon><CalendarDots size={22} /></InputLeftAddon>
                <Input
                    {...register('end_date')}
                    as={InputMask}
                    mask="99/99/9999"
                    placeholder='__/__/____'
                    borderRadius="8px"
                    fontSize="md"
                />
            </InputGroup>
        },
    ]

    const customActions = [
        {
            label: "Enviar Mensagem",
            callback: (id: string) => onOpen(),
            icon: <WhatsappLogo weight="thin" />,
            color: "green"
        }
    ]

    return (
        <>
            <FormProvider {...methods}>
                <Flex direction="column" w="full">
                    <TableFilterBox fields={filterFields} handleClear={handleResetFilter}  handleFilter={handleFilter} />
                    <Divider py={4} />
                    <AsyncTable 
                        isLoading={loading || reloading} 
                        header={header} 
                        per_page={paginatorInfo.perPage}
                        current_page={paginatorInfo.currentPage}
                        total={paginatorInfo.total}
                        data={data}
                        onPaginationChange={onPaginationChange}
                        customActions={highRisk ? customActions : []}
                        noActions
                        linkRow 
                    />
                    <SendMessage isOpen={isOpen} onClose={onClose} id={""} />
                </Flex>
            </FormProvider>
        </>
    )
}

export default LivesTable